import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [error, setError] = useState('');
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const permitirRegistro = false;
  const sessionExpired = searchParams.get('reason') === 'inactivity';

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    setError('');

    const { error, mfaRequired: precisaMfa } = await signIn(email.trim().toLowerCase(), password, mfaCode);
    
    if (error) {
      if (precisaMfa) {
        setMfaRequired(true);
        setError(error.message || 'Informe o código enviado para o email cadastrado');
        setLoading(false);
        return;
      }
      if (error.message.includes('Invalid login credentials') || error.message.includes('Email ou senha invalidos')) {
        setError('Email ou senha incorretos');
      } else if (error.message.includes('Email not confirmed')) {
        setError('Por favor, confirme seu email antes de fazer login');
      } else {
        setError(error.message || 'Erro ao fazer login. Tente novamente.');
      }
    } else {
      setMfaRequired(false);
      setMfaCode('');
      toast({
        title: "Login realizado com sucesso!",
        description: "Bem-vindo ao Cloud Odonto",
      });
    }
    
    setLoading(false);
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailToUse = resetEmail || email;
    if (!emailToUse) {
      setError('Informe seu email para solicitar o reset');
      return;
    }

    setResetLoading(true);
    setError('');
    try {
      const response = await fetch('/api/auth-users.php?action=request_reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToUse.trim().toLowerCase() }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) throw new Error(data.error || 'Erro ao solicitar reset');
      toast({
        title: 'Solicitacao enviada',
        description: 'O administrador da conta devera redefinir sua senha.',
      });
      setShowReset(false);
      setResetEmail('');
    } catch (err: any) {
      setError(err.message || 'Erro ao solicitar reset. Tente novamente.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    const { error } = await signUp(email, password);
    
    if (error) {
      if (error.message.includes('User already registered')) {
        setError('Este email já está cadastrado');
      } else {
        setError('Erro ao criar conta. Tente novamente.');
      }
    } else {
      toast({
        title: "Conta criada com sucesso!",
        description: "Verifique seu email para confirmar a conta",
      });
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-muted p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/cloud-odonto-logo.png" alt="Cloud Odonto" className="mx-auto mb-4 h-auto w-full max-w-[300px] object-contain dark:hidden" />
          <img src="/cloud-odonto-logo-dark.png" alt="Cloud Odonto" className="mx-auto mb-4 hidden h-auto w-full max-w-[300px] object-contain dark:block" />
          <h1 className="sr-only">Cloud Odonto</h1>
          <p className="text-muted-foreground mt-2">Sistema de Gestão Odontológica</p>
        </div>

        <Card className="medical-card">
          <CardHeader className="text-center">
            <CardTitle>Acesse sua conta</CardTitle>
            <CardDescription>
              Entre com suas credenciais ou crie uma nova conta
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin" className="w-full">
              {permitirRegistro ? (
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="signin">Entrar</TabsTrigger>
                  <TabsTrigger value="signup">Cadastrar</TabsTrigger>
                </TabsList>
              ) : (
                <TabsList className="grid w-full grid-cols-1">
                  <TabsTrigger value="signin">Entrar</TabsTrigger>
                </TabsList>
              )}

              {error && (
                <Alert variant="destructive" className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {sessionExpired && !error && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Sua sessão foi encerrada após 30 minutos sem atividade. Entre novamente para continuar.
                  </AlertDescription>
                </Alert>
              )}

              <TabsContent value="signin">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <Input
                      id="signin-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Senha</Label>
                    <Input
                      id="signin-password"
                      type="password"
                      placeholder="Sua senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                  {mfaRequired && (
                    <div className="space-y-2">
                      <Label htmlFor="signin-mfa">Código de segurança</Label>
                      <Input
                        id="signin-mfa"
                        inputMode="numeric"
                        placeholder="Código enviado por email"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                      />
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>
                  <Button type="button" variant="link" className="w-full px-0" onClick={() => { setShowReset(!showReset); setResetEmail(email); }}>
                    Esqueci minha senha
                  </Button>
                </form>

                {showReset && (
                  <form onSubmit={handleResetRequest} className="mt-4 space-y-3 rounded-md border p-3">
                    <div className="space-y-2">
                      <Label htmlFor="reset-email">Email da conta</Label>
                      <Input
                        id="reset-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" variant="outline" className="w-full" disabled={resetLoading}>
                      {resetLoading ? 'Enviando...' : 'Solicitar reset ao administrador'}
                    </Button>
                  </form>
                )}
              </TabsContent>

              {permitirRegistro && (
                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Senha</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="Mínimo 6 caracteres"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loading}
                    >
                      {loading ? 'Criando conta...' : 'Criar conta'}
                    </Button>
                  </form>
                </TabsContent>
              )}
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Sistema seguro e confiável para gestão odontológica
        </p>
      </div>
    </div>
  );
}
